// Theme Context - Dark Mode Support
import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('system'); // 'light', 'dark', 'system'
  const [resolvedTheme, setResolvedTheme] = useState('dark');

  // Get system preference
  const getSystemTheme = useCallback(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'dark';
  }, []);

  // Resolve actual theme based on setting
  const resolveTheme = useCallback((themeSetting) => {
    if (themeSetting === 'system') {
      return getSystemTheme();
    }
    return themeSetting;
  }, [getSystemTheme]);

  // Load saved preference
  useEffect(() => {
    const saved = localStorage.getItem('salesspace-theme');
    if (saved && ['light', 'dark', 'system'].includes(saved)) {
      setTheme(saved);
      setResolvedTheme(resolveTheme(saved));
    } else {
      setResolvedTheme(resolveTheme('system'));
    }
  }, [resolveTheme]);

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme === 'system') {
        setResolvedTheme(getSystemTheme());
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, getSystemTheme]);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    
    if (resolvedTheme === 'dark') {
      root.classList.add('dark');
      root.style.colorScheme = 'dark';
    } else {
      root.classList.remove('dark');
      root.style.colorScheme = 'light';
    }
  }, [resolvedTheme]);

  // Change theme
  const changeTheme = useCallback((newTheme) => {
    setTheme(newTheme);
    setResolvedTheme(resolveTheme(newTheme));
    localStorage.setItem('salesspace-theme', newTheme);
  }, [resolveTheme]);

  // Toggle between light and dark
  const toggleTheme = useCallback(() => {
    const newTheme = resolvedTheme === 'dark' ? 'light' : 'dark';
    changeTheme(newTheme);
  }, [resolvedTheme, changeTheme]);

  return (
    <ThemeContext.Provider value={{
      theme,
      resolvedTheme,
      isDark: resolvedTheme === 'dark',
      changeTheme,
      toggleTheme
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export default ThemeContext;
